import axios, { AxiosInstance, AxiosResponse, AxiosError } from "axios";
import Config from "../Config";

const AT_TOKEN = "ATToken";

type TokenType = {
  access_token: string;
  refresh_token: string;
};
export const getToken = (): TokenType | null => {
  try {
    const tokenStr = localStorage.getItem(AT_TOKEN); // it should return the bar
    let token = null;
    if (tokenStr) {
      token = JSON.parse(tokenStr);
    }
    return token;
  } catch (e) {
    return null;
  }
};
const setToken = (token: TokenType | null) => {
  if (token) {
    localStorage.setItem(AT_TOKEN, JSON.stringify(token)); // it should return the bar
  } else {
    localStorage.removeItem(AT_TOKEN);
  }
};

export const refreshAccessToken = async (): Promise<TokenType | null> => {
  const token = getToken();
  let newToken = null;
  if (token) {
    const url = `${Config.apiPath}/auth/refresh`;
    try {
      let res = await axios.post(url, null, {
        headers: {
          Authorization: `Bearer ${token.refresh_token}`,
        },
      });
      newToken = { ...token, ...res.data };
    } catch (e) {
      newToken = null;
    }
  }
  setToken(newToken);
  return newToken as TokenType | null;
};

export const login = async (
  username: string,
  password: string,
): Promise<TokenType> => {
  const url = `${Config.apiPath}/auth/login`;
  const response = await axios.post(url, {
    username: username.trim(),
    password: password, // Don't trim passwords - preserve exact characters
  });

  if (response.data.access_token && response.data.refresh_token) {
    const tokenData = {
      access_token: response.data.access_token,
      refresh_token: response.data.refresh_token,
    };
    setToken(tokenData);
    return tokenData;
  } else {
    throw new Error("Login failed");
  }
};

export const register = async (
  username: string,
  email: string,
  password: string,
  firstName?: string,
  lastName?: string,
  captcha?: string,
): Promise<any> => {
  const url = `${Config.apiPath}/auth/register`;
  const response = await axios.post(url, {
    username: username.trim(),
    email: email.trim(),
    password: password, // Don't trim passwords - preserve exact characters
    first_name: firstName?.trim() || "",
    last_name: lastName?.trim() || "",
    captcha: captcha || "",
  });

  return response.data;
};

export const setInterceptor = (axiosInstance: AxiosInstance) => {
  function isTokenExpiredError(error: AxiosError): boolean {
    return error.response?.status === 401;
  }

  axiosInstance.interceptors.request.use(
    (config) => {
      const token = getToken(); // Implement a function to get the access token
      if (token) {
        config.headers.Authorization = `Bearer ${token.access_token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    },
  );

  axiosInstance.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    async (error: AxiosError) => {
      const originalRequest = error.config;

      // Check if the error is due to an expired or invalid token
      if (isTokenExpiredError(error)) {
        // Implement a function to refresh the access token and get the new token
        const newAccessToken: TokenType | null = await refreshAccessToken();
        if (newAccessToken) {
          // Update the original request with the new access token
          originalRequest.headers.Authorization = `Bearer ${newAccessToken.access_token}`;
          // Retry the original request with the new token
          return axiosInstance(originalRequest);
        }
      }
      // Handle other error cases
      return Promise.reject(error);
    },
  );
  return axiosInstance;
};
